<template>
  <div>
    <ShareTemplateErrors :form="form"
                         :template="template">
    </ShareTemplateErrors>

    <v-card class="mb-4">
      <v-card-title>
        Status and availability
      </v-card-title>
      <v-card-text>
        <v-row class="">
          <v-col cols="12"
                 md="4">
            <v-select v-model="form.status"
                      label="Status"
                      :items="statusCodes"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense>
            </v-select>
          </v-col>
          <v-col cols="12"
                 md="4">
            <v-menu ref="releaseAtMenu"
                    v-model="releaseAtMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                    z-index="999">
              <template v-slot:activator="{on, attrs}">
                <v-text-field v-model="form.release_at"
                              label="Release date (Optional)"
                              placeholder="Optional"
                              prepend-icon="fal fa-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              clearable
                              hint="Leave this blank to make it available now"
                              persistent-hint
                              outlined
                              dense>
                </v-text-field>
              </template>
              <v-date-picker v-model="form.release_at"
                             no-title
                             scrollable
                             @input="releaseAtMenu = false; expiresAtMenu = true;">
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12"
                 md="4">
            <v-menu ref="expiresAtMenu"
                    v-model="expiresAtMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                    z-index="999">
              <template v-slot:activator="{on, attrs}">
                <v-text-field v-model="form.expires_at"
                              :label="team.share_template_expiration_required ? 'Expiration date (Required)' : 'Expiration date (Optional)'"
                              :placeholder="team.share_template_expiration_required ? 'Required' : 'Optional'"
                              prepend-icon="fal fa-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              clearable
                              outlined
                              dense
                              :rules="expirationValidationRules"
                              :error-messages="form.errors.get('expires_at')">
                </v-text-field>
              </template>
              <v-date-picker v-model="form.expires_at"
                             no-title
                             scrollable
                             @input="expiresAtMenu = false">
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title>
        Who can use this template?
      </v-card-title>
      <v-card-text>
        <v-radio-group v-model="form.accessControl"
                       class="mt-0">
          <v-radio value="Groups"
                   label="Specific groups">
          </v-radio>
          <v-radio value="User"
                   label="One user">
          </v-radio>
          <v-radio value="Everyone"
                   label="Everyone">
          </v-radio>
        </v-radio-group>

        <div v-if="form.accessControl === 'Groups'"
             class="mb-4">
          <v-autocomplete v-model="form.groups"
                          label="Restrict access to these groups"
                          :items="groups"
                          item-value="id"
                          item-text="name"
                          hint="If you don't select any groups, then every group will have access to this template."
                          persistent-hint
                          outlined
                          dense
                          multiple
                          chips
                          deletable-chips
                          small-chips
                          clearable>
          </v-autocomplete>
        </div>

        <v-autocomplete v-else-if="form.accessControl === 'User'"
                        v-model="form.user_id"
                        label="Owner"
                        :items="activeUsers"
                        item-value="id"
                        item-text="name"
                        hint="If this template belongs to a specific user, select them here. Only they will be able to see or use it."
                        persistent-hint
                        outlined
                        dense
                        clearable>
        </v-autocomplete>
      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title>
        Template details
      </v-card-title>
      <v-card-text>

        <AllerganEmailTemplateNamingConventions>
        </AllerganEmailTemplateNamingConventions>

        <v-text-field v-model="form.name"
                      label="What is the name of this template?"
                      placeholder=" "
                      hint="This must be unique."
                      outlined
                      dense
                      :rules="[rules.required]"
                      :error-messages="form.errors.get('name')">
        </v-text-field>

        <v-text-field v-model="form.asset_id"
                      label="Asset ID (Optional)"
                      hint="Your company's unique identifier for this template (Optional)"
                      outlined
                      dense>
        </v-text-field>

        <v-autocomplete v-if="team.brands_enabled"
                        v-model="form.brand_id"
                        label="Which brand is this template associated with?"
                        hint="This is optional."
                        :items="brands"
                        item-value="id"
                        item-text="name"
                        outlined
                        dense
                        clearable>
        </v-autocomplete>

        <v-select v-if="showFolderField"
                  v-model="form.folder_id"
                  label="Which folder should this template be in? (Optional)"
                  :items="folderTree"
                  item-value="id"
                  item-text="name"
                  outlined
                  dense
                  clearable>
          <template v-slot:selection="{item: folder}">
            <v-icon left>
              fal fa-folder-open
            </v-icon>
            {{ folder.name }}
          </template>
          <template v-slot:item="{item: folder}">
            <template v-if="folder.depth === 0">
              <v-icon left>
                fal fa-folder
              </v-icon>
              {{ folder.name }}
            </template>
            <template v-else>
              <v-icon v-for="depth in folder.depth"
                      :key="depth">
                fal fa-fw
              </v-icon>
              <v-icon left>
                fal fa-folder
              </v-icon>
              {{ folder.name }}
            </template>
          </template>
        </v-select>

        <v-autocomplete v-model="form.locale"
                        label="Language"
                        :items="locales"
                        item-value="id"
                        item-text="name"
                        outlined
                        dense
                        :rules="[rules.required]"
                        :error-messages="form.errors.get('locale')">
        </v-autocomplete>

        <v-row>
          <v-col cols="12">
            <div>
              Provide instructions for how this template should or should not be used. Displayed when a user composes their message.
            </div>
            <v-textarea v-model="form.instructions"
                        rows="2"
                        placeholder="Optional instructions"
                        hide-details
                        no-resize
                        outlined
                        dense>
            </v-textarea>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <div>
              Subject line(s)
            </div>
            <v-list dense>
              <draggable v-model="form.subjects"
                         handle=".handle">
                <v-list-item v-for="(subjectLine, index) in form.subjects"
                             :key="index"
                             class="pl-0 border-gray-light">
                  <v-list-item-icon class="mt-5 mb-0 mx-2 handle"
                                    style="cursor: grab;">
                    <v-icon>
                      fas fa-fw fa-grip-lines
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-text-field v-model="form.subjects[index].subject"
                                  :label="index === 0 ? `Subject line ${index + 1} (Default)` : `Subject line ${index + 1}`"
                                  autocomplete="new-subject-line"
                                  outlined
                                  dense
                                  hide-details
                                  @blur="checkSubjectLine(index)"
                                  @keydown.enter.prevent="">
                    </v-text-field>
                  </v-list-item-content>
                  <v-list-item-action class="d-flex flex-row align-center ml-2">
                    <v-btn icon
                           color="purple"
                           @click.prevent="openSubjectLineDialog(index)"
                           title="Generate alternatives with AI">
                      <v-icon>
                        fas fa-fw fa-sparkles
                      </v-icon>
                    </v-btn>
                    <v-btn v-if="index === (form.subjects.length - 1)"
                           icon
                           :disabled="!form.subjects[index].subject"
                           color="success"
                           @click.prevent="addAnotherSubject(index)"
                           title="Add another subject line">
                      <v-icon>
                        fas fa-fw fa-plus-circle
                      </v-icon>
                    </v-btn>
                    <v-btn icon
                           @click.prevent="deleteSubject(index)"
                           :disabled="form.subjects.length < 2"
                           color="error"
                           title="Delete this subject line">
                      <v-icon>
                        fas fa-fw fa-trash
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </draggable>
            </v-list>

            <v-checkbox v-model="form.is_subject_editable"
                        :disabled="form.subjects.length > 1"
                        label="A user can edit the subject line"
                        hint="Not available where there is more than one subject line option."
                        persistent-hint
                        class="">
            </v-checkbox>
          </v-col>
        </v-row>

        <v-row v-if="1===2 && template">
          <v-col cols="12">
            <v-btn @click.prevent="openSubjectLineDialog"
                   class="mb-6"
                   small>
              Edit subject lines
            </v-btn>
          </v-col>
        </v-row>

      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-text>
        <v-select v-model="form.editor"
                  label="Editor"
                  :items="editors"
                  item-value="id"
                  item-text="name"
                  outlined
                  dense
                  :rules="[rules.required]"
                  :error-messages="form.errors.get('editor')"
                  hint="If you want a user to edit or tweak the email before sending it, you must select TinyMCE or Unlayer as the editor. They cannot edit or tweak Raw HTML emails."
                  persistent-hint
                  @change="changeEditor"
                  class="mb-4">
        </v-select>

        <v-text-field v-if="isUnlayer === false"
                      v-model="form.preheader_text"
                      label="Preview text (Optional)"
                      hint="Email preview is the line of text shown after the subject line of an email in the inbox."
                      persistent-hint
                      outlined
                      dense
                      :error-messages="form.errors.get('preheader_text')"
                      counter
                      max-length="100"
                      class="mb-4">
          <template v-slot:message="{key, message}">
            <span class="mr-1">
              {{ message }}
            </span>
            <a href="https://www.litmus.com/blog/the-ultimate-guide-to-preview-text-support/"
               target="_blank">
              Read about email preview text best practices
            </a>
          </template>
        </v-text-field>

        <v-dialog v-if="tagsDialog"
                  v-model="tagsDialog"
                  scrollable
                  max-width="90%">
          <v-card>
            <v-card-title class="primary white--text">
              Tags
              <v-spacer></v-spacer>
              <v-btn @click.prevent="closeTagsDialog">
                Close
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="ml-4 mt-4">
                Mobile Locker user the <a href="https://shopify.github.io/liquid/basics/introduction/"
                                          target="_blank">Liquid template language</a>.
              </div>
              <TagsTable @selected="insertSelectedTag"
                         :template="template">
              </TagsTable>
            </v-card-text>
          </v-card>
        </v-dialog>

        <div v-if="isRaw"
             class="form-group"
             :class="{'has-error': form.errors.has('html_body')}">
          <label>Raw HTML</label>
          <div class="my-1">
            <v-btn @click.prevent="openTagsDialog">
              Insert Tags
            </v-btn>
          </div>
          <codemirror v-model="form.html_body"
                      :options="cmOptions"
                      id="EmailTemplateEditor"
                      placeholder="Raw HTML">
          </codemirror>
          <div class="text-caption">
            Make sure your code starts with <code>&lt;!DOCTYPE HTML</code>
          </div>
        </div>
        <template v-else-if="isTinyMCE">
          <div class="mb-4">
            <label>
              Email Message Body
            </label>
            <div class="text-caption">
              Read instructions below.
            </div>
            <tinymce-editor api-key="w7qx9l4xwuc114tjfvqngdobxbi48cjwt782qof3qt0ht031"
                            id="tm3"
                            :init="tinyConfig"
                            v-model="form.html_body">
            </tinymce-editor>
            <div class="text-caption mb-2">
              If you paste raw HTML code, only paste the contents of the <code>&lt;body&gt;</code> tag into the
              TinyMCE editor. You can paste the contents of the <code>&lt;head&gt;</code> tag under the <strong>Advanced
              Settings</strong> section below. If you need to paste raw HTML, click <code>Tools &gt; Source Code</code>.
            </div>
            <div class="text-caption mb-2">
              By default, a user can edit any part of the template. To mark sections of your template as non-editable,
              add the <code>mceNonEditable</code> class to the element. To mark sections of your template as explicitly
              editable, add the <code>mceEditable</code> class to the element. <a
              href="https://docs.mobilelocker.com/docs/share-templates#make-sections-of-your-template-non-editable"
              target="_blank">Learn more here</a>.
            </div>

          </div>
        </template>

        <div v-else-if="isUnlayer">
          <div v-if="template">
            <v-btn :to="{name: 'admin.share_template.unlayer.edit', params: {templateID: template.id}}">
              Edit Template
            </v-btn>
          </div>
          <div v-else>
            You can edit the template after you save it.
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card v-if="template"
            class="mb-4"
            :loading="busy">
      <v-card-title>
        <div>
          Custom Tags
        </div>
      </v-card-title>
      <v-card-text v-if="customTags.length > 0">
        <ShareTemplateCustomTagsTable v-if="busy === false"
                                      :custom-tags="customTags"
                                      :template="template"
                                      class="border"
                                      @edit="openCustomTagDialog"
                                      @update="fetchCustomTags">
        </ShareTemplateCustomTagsTable>
      </v-card-text>
      <v-card-actions class="px-4">
        <v-btn v-if="1===2"
               :to="{name: 'admin.share_template_custom_tag.create', query: {template_id: this.template.id}}">
          New Custom Tag
        </v-btn>
        <v-btn @click.prevent="openCustomTagDialog(null)">
          New Custom Tag
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-if="1===2 && canTestEmailTemplates && form.html_body && template && template.id"
            class="mb-4">
      <v-card-title>
        Email Preview
      </v-card-title>
      <v-card-text>
        <template v-if="emailPreviewsEnabled">
          Be certain your messages display as intended—on every device, in every inbox. Deliver error-free messages in less time with testing on 100+ devices and clients (including dark mode).
        </template>
        <template v-else>
          Contact Mobile Locker support (support@mobilelocker.com) if you want to turn this on.
        </template>
      </v-card-text>
      <v-card-actions class="px-4">
        <v-btn :to="{name: 'admin.mailgun.email_preview.create', query: {template_id: template.id}}">
          Create Test
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-if="spamTestEnabled && form.html_body && template"
            class="mb-4">
      <v-card-title>
        Spam Testing
      </v-card-title>
      <v-card-text class="pb-0">
        <p>
          <a href="https://help.mobilelocker.com/article/201-how-to-check-an-email-template-for-spam" target="_blank">What is this?</a>
        </p>
        <template v-if="!team.spam_test_enabled">
          <v-alert type="warning"
                   tile
                   class="mb-0">
            Contact Mobile Locker support (support@mobilelocker.com) if you want to turn this on.
          </v-alert>
        </template>
      </v-card-text>
      <v-card-actions class="px-4">
        <CheckSpamDialog :form="form"
                         :template="template">
        </CheckSpamDialog>
      </v-card-actions>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="pb-0">
        Where can this template be used?
      </v-card-title>
      <v-card-text>
        <p>
          In some cases, you may want to restrict where the templates can be used.
        </p>
        <v-switch v-model="form.is_web"
                  label="In the browser"
                  hide-details>
        </v-switch>
        <v-switch v-model="form.is_app"
                  label="In the iOS, Windows, and macOS apps"
                  hide-details>
        </v-switch>
        <v-switch v-if="team.chrome_extension_enabled"
                  v-model="form.is_gmail"
                  label="In Gmail"
                  hide-details>
        </v-switch>
        <v-switch v-if="team.outlook_plugin_enabled"
                  v-model="form.is_outlook"
                  label="In Microsoft Outlook"
                  hide-details>
        </v-switch>
      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="pb-0">
        Other Settings
      </v-card-title>
      <v-card-text>
        <v-switch v-model="form.is_featured"
                  label="This is a featured template."
                  hint="When you view a list of templates, they are usually ordered alphabetically. But if you turn on this option, you can make this specific template appear at the very top of the list. If you have multiple featured templates, they will still be listed alphabetically, but they will be placed above the non-featured templates."
                  persistent-hint>
        </v-switch>
        <v-switch v-model="form.can_be_selected"
                  :label="form.can_be_selected ? 'Will appear in the list of email templates.' : 'Will not appear in the list of email templates.'"
                  hint="If you want others to be able to use your template, turn on this option. But keep in mind that the user needs to be in the right groups. If you don't want your template to be visible in the list, just turn off this option. However, it's recommended to keep it on for most templates."
                  persistent-hint>
        </v-switch>
        <v-switch v-model="form.is_editable"
                  :label="form.is_editable ? 'A user can edit this template when sending it to an individual.' : 'A user cannot edit this template when sending it to an individual.'"
                  :hint="form.is_editable ? 'The sender will be able to edit the content of this template.' :  'The sender will not be able to edit the content in this template.'"
                  persistent-hint
                  :disabled="isRaw"
                  @change="changeIsEditable">
        </v-switch>
        <v-switch v-model="form.can_share_links"
                  :label="form.can_share_links ? 'A user can insert share links into this template.' : 'A user cannot insert share links into this template.'"
                  :hint="form.can_share_links ? 'The sender will be able to share links in the body of the email.' : 'The sender will not be able to share links in the body of the email.'"
                  persistent-hint>
        </v-switch>
        <template v-if="isCampaignsEnabled">
          <v-switch v-model="form.is_campaign"
                    :label="form.is_campaign ? 'A user can send this template to one person at a time or to many people at once.' : 'A user can only send this template to one person at a time.'"
                    :hint="'When sending a campaign, the sender cannot edit the content of the template or insert links, regardless of the the previous two questions.'"
                    persistent-hint>
          </v-switch>
        </template>

        <v-switch v-model="form.personal_message_allowed"
                  :label="form.personal_message_allowed ? 'A user can write a custom message that will be included in the body of the email.' : 'A user cannot write a custom message that will be included in the body of the email.'"
                  :hint="customMessageHint"
                  persistent-hint>
        </v-switch>
        <v-switch v-if="form.personal_message_allowed"
                  v-model="form.personal_message_required"
                  :label="form.personal_message_allowed ? 'A user must write a custom message that will be included in the body of the email.' : 'A user can write a custom message but doesn\'t have to.'"
                  hide-details>
        </v-switch>

        <v-switch v-model="form.can_be_copied"
                  label="This template can be copied.">
        </v-switch>
      </v-card-text>
    </v-card>

    <template v-if="isQuickShareEnabled">
      <v-card class="mb-4">
        <v-card-title class="pb-0">
          Is this a Quick-Share template?
        </v-card-title>
        <v-card-text>
          <p>
            A quick-share template is used to send a single file, without requiring or allowing the user to edit the body
            or edit any content in the email.
          </p>
          <v-switch v-model="form.is_quick_share"
                    label="Yes, this is a quick-share template">
          </v-switch>
        </v-card-text>
      </v-card>

      <v-card v-if="form.is_quick_share"
              class="mb-4">
        <v-card-title class="pb-0">
          Is this our default Quick-Share template?
        </v-card-title>
        <v-card-text>
          <p>
            The default quick-share template will be used when quick-sharing any presentation that does not explicitly
            override it. The default quick-share template cannot selected when sending a normal email. If you do not set a
            default template, then the system default one will be used.
          </p>
          <v-switch v-model="form.is_default"
                    label="This is the our default quick-share template">
          </v-switch>
        </v-card-text>
      </v-card>

      <v-card class="mb-4"
              v-if="form.is_quick_share">
        <v-card-title>
          This is the Quick-Share template for these files
        </v-card-title>
        <v-card-text>
          <p>
            When a user tries to share a file via email, you can require them to use this template as the quick-share
            template. If you associate a file with this template, users will not be able to share it using other templates
            or free-text emails.
          </p>
          <v-autocomplete v-model="form.presentations"
                          label="Which files require this as the quick-share template?"
                          :items="presentations"
                          item-value="id"
                          item-text="name"
                          multiple
                          chips
                          deletable-chips
                          clearable>
          </v-autocomplete>
        </v-card-text>
      </v-card>
    </template>

    <template v-if="form.presentations.length && [1, 65].includes(team.id)">
      <v-card class="mb-4">
        <v-card-text>
          <v-radio-group v-model="form.attach_file"
                         label="Attach the file to the email?"
                         class="mt-0"
                         hint="You can only attach the file to the email if it is a PDF. If you attach the file to the email, you will not be able to track the recipient's interacts with the file. Please note some recipients may not be able to receive large attachments, so you might want to link to the file instead."
                         persistent-hint>
            <v-radio :value="true"
                     label="Yes, attach the file to the email.">
            </v-radio>
            <v-radio :value="false"
                     label="No, do not attach the file to the email.">
            </v-radio>
          </v-radio-group>

          <v-radio-group v-model="form.sanitize_attachment_name"
                         label="Sanitize the attachment filename?"
                         class="">
            <v-radio :value="true"
                     label="Yes, sanitize the attachment filename as [presentation-name].pdf">
            </v-radio>
            <v-radio :value="false"
                     label="No, name the attachment file [Presentation Name].pdf">
            </v-radio>
          </v-radio-group>
        </v-card-text>
      </v-card>
    </template>

    <v-card v-if="isTinyMCE || isUnlayer"
            class="mb-4">
      <v-card-title>
        Advanced settings
      </v-card-title>
      <v-card-text>
        <div v-if="isTinyMCE"
             class="mb-4">
          <label>
            Email HTML HEAD (Optional)
          </label>

          <div class="text-caption mb-2">
            If you want the email to be responsive, Paste the <code>&lt;style&gt;</code> tags here. Do not include
            the <code>&lt;head&gt;</code> tag itself, but you should include all necessary <code>&lt;meta&gt;</code>
            tags, too.
          </div>

          <div>
            <code style="display: block;">&lt;head&gt;</code>
          </div>
          <codemirror v-model="form.html_head"
                      :options="cmOptions"
                      placeholder="Optional value of the HEAD tag.">
          </codemirror>
          <div>
            <code style="display: block;">&lt;/head&gt;</code>
          </div>

        </div>

        <div class="mb-4">
          <label>
            Email CSS to Inline (Optional)
          </label>

          <div class="text-caption mb-2">
            If you want to convert CSS to inline styles in the body of the HTML, paste your custom CSS code here. Do not include the <code>&lt;style&gt;</code> tags.
          </div>

          <div>
            <code style="display: block;">&lt;style&gt;</code>
          </div>
          <codemirror v-model="form.css"
                      :options="cssOptions"
                      placeholder="Optional value of the STYLE tag.">
          </codemirror>
          <div>
            <code style="display: block;">&lt;/style&gt;</code>
          </div>

        </div>

        <v-text-field v-model="form.shared_link_url_parameters"
                      label="URL Parameters"
                      placeholder="OPTIONAL: utm_campaign=CampaignName&utm_medium=email"
                      hint="When a user inserts a dynamic link into their email template, append these URL parameters to the URL."
                      persistent-hint
                      outlined
                      dense
                      autocomplete="off">
        </v-text-field>

        <v-text-field v-if="team.sharing_bcc_field_enabled"
                      v-model="form.bcc_recipients"
                      label="BCC Recipients"
                      hint="BCC the email to these addresses. Separate multiple email addresses with commas."
                      persistent-hint
                      outlined
                      dense
                      :error-messages="form.errors.get('bcc_recipients')"
                      autocomplete="off">
        </v-text-field>

        <v-text-field v-if="team.sharing_cc_field_enabled"
                      v-model="form.cc_recipients"
                      label="CC Recipients"
                      hint="CC the email to these addresses. Separate multiple email addresses with commas."
                      persistent-hint
                      outlined
                      dense
                      :error-messages="form.errors.get('cc_recipients')"
                      autocomplete="off">
        </v-text-field>

        <v-text-field v-model="form.attachment_url"
                      type="url"
                      label="Calendar Invitation Attachment URL"
                      hint="If you want to attach a calendar invitation file (ICS), paste its URL here."
                      :placeholder="`${team.assets_url}/some-invitation.ics`"
                      persistent-hint
                      outlined
                      dense
                      :error-messages="form.errors.get('attachment_url')"
                      autocomplete="off">
        </v-text-field>
      </v-card-text>
    </v-card>

    <v-card v-if="template && template.id"
            class="mb-4">
      <v-card-text>
        <v-text-field v-model="template.send_email_url"
                      label="Send Email URL"
                      outlined
                      dense
                      readonly
                      append-icon="fal fa-copy"
                      @click:append="copyURL(template.send_email_url)"
                      hint="Copy this URL if you want to make it easy for a user to send an email to one person using this template."
                      persistent-hint
                      class="mb-4">
        </v-text-field>

        <v-text-field v-if="team.campaigns_enabled && form.is_campaign"
                      v-model="template.send_campaign_url"
                      label="Send Campaign URL"
                      outlined
                      dense
                      readonly
                      append-icon="fal fa-copy"
                      @click:append="copyURL(template.send_campaign_url)"
                      hint="Copy this URL if you want to make it easy for a user to send a campaign email to many people at once using this template."
                      persistent-hint
                      class="mb-4">
        </v-text-field>

        <v-text-field v-model="template.preview_url"
                      label="Preview Template URL"
                      outlined
                      dense
                      readonly
                      append-icon="fal fa-copy"
                      @click:append="copyURL(template.preview_url)"
                      class="mb-4"
                      hide-details>
        </v-text-field>
      </v-card-text>
    </v-card>

    <ShareTemplateErrors :form="form"
                         :template="template">
    </ShareTemplateErrors>

    <v-dialog v-model="pickerDialog"
              max-width="500">
      <v-form ref="pickerForm"
              @submit.prevent="savePicker"
              :disabled="pickerForm.busy">
        <v-card>
          <v-card-text>
            <v-text-field v-model="pickerForm.name"
                          label="Name"
                          required
                          :rules="[rules.required]"
                          :errors="pickerForm.errors.get('name')">
            </v-text-field>

            <template v-for="(option, index) in pickerForm.options">
              <v-text-field :key="option"
                            v-model="pickerForm.options[index]"
                            :label="`Option ${index+1}`"
                            placeholder=" "
                            append-icon="fal fa-trash"
                            required
                            :rules="[rules.required]"
                            @click:append="removeOption(option)">
              </v-text-field>
            </template>

            <v-text-field v-model="pickerForm.new_option"
                          label="New option"
                          @keydown.enter.prevent="addOption"
                          :append-icon="pickerForm.new_option !== '' ? 'fal fa-plus' : ''"
                          @click:append="addOption">
            </v-text-field>

          </v-card-text>
          <v-card-actions class="px-4">
            <v-btn href=""
                   target="_blank">
              Help
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary"
                   :disabled="pickerForm.options.length === 0 || pickerForm.new_option !== ''"
                   type="submit">
              Save Dropdown
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <ShareTemplateCustomTagDialog v-if="template && customTagDialog"
                                  :dialog="customTagDialog"
                                  :template="template"
                                  :custom-tag="customTag"
                                  @close="closeCustomTagDialog">
    </ShareTemplateCustomTagDialog>

    <SuggestSubjectLineDialog v-if="subjectLineDialog"
                              :dialog="subjectLineDialog"
                              :subject="subjectForm.subject"
                              :index="subjectForm.index"
                              @close="closeSubjectLineDialog">
    </SuggestSubjectLineDialog>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import tinymceService from '../../../../../ml/services/tinymceService';
import Codemirror from './CodeMirror';
import dataService from '../../../../../ml/services/dataService';
import formValidation from '../../../plugins/formValidation';
import TagsTable from './TagsTable';
import mobilelockerEditor from './tinymceMobileLocker';
import {isNull, isObject, isString} from 'lodash';
import clipboardService from "../../../../../ml/services/clipboardService";
import CheckSpamDialog from './CheckSpamDialog';
import ShareTemplateErrors from './ShareTemplateErrors.vue';
import AllerganEmailTemplateNamingConventions from './AllerganEmailTemplateNamingConventions.vue';
import ShareTemplateCustomTagsTable from '../../share_template_custom_tags/components/ShareTemplateCustomTagsTable.vue';
import shareTemplateService from '../../../../../ml/services/shareTemplateService';
import ShareTemplateCustomTagDialog from '../../share_template_custom_tags/components/ShareTemplateCustomTagDialog.vue';
import draggable from 'vuedraggable';
import SuggestSubjectLineDialog from './SuggestSubjectLineDialog.vue';

export default {
  name: 'share-template-form',
  components: {SuggestSubjectLineDialog, draggable, ShareTemplateCustomTagDialog, ShareTemplateCustomTagsTable, AllerganEmailTemplateNamingConventions, ShareTemplateErrors, CheckSpamDialog, TagsTable, Codemirror},
  mixins: [formValidation],
  props: {
    form: {
      type: Object,
      required: true,
    },
    template: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      busy: false,
      releaseAtMenu: false,
      expiresAtMenu: false,
      cmOptions: { // MLD-2620
        autoCloseTags: true,
        extraKeys: {'Ctrl-Space': 'autocomplete'},
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
        lineNumbers: true,
        lineWrapping: false,
        lint: true,
        matchTags: {bothTags: true},
        mode: 'htmlmixed',
        theme: 'monokai',
      },
      cssOptions: { // MLD-2910
        autoCloseTags: true,
        extraKeys: {'Ctrl-Space': 'autocomplete'},
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
        lineNumbers: true,
        lineWrapping: false,
        lint: true,
        matchTags: {bothTags: true},
        mode: 'css',
        theme: 'monokai',
      },
      editor: null,
      editors: [ // MLD-2473
        {id: 'tinymce', name: 'TinyMCE (Default)'},
        {id: 'unlayer', name: 'Unlayer (Drag-and-drop)'},
        {id: 'raw', name: 'Raw HTML (Advanced)'},
      ],
      statusCodes: dataService.statusCodes(),
      tagsDialog: false,
      pickerDialog: false,
      pickerForm: new Form({
        name: '',
        new_option: '',
        options: [],
      }),
      spamTestBusy: false, // MLD-4012
      spamResult: null, // MLD-4012
      customTags: [], // MLD-6454
      customTag: null, // MLD-6454
      customTagDialog: false, // MLD-6454
      subjectLines: [], // MLD-6517
      subjectLineDialog: false, // MLD-6517
      subjectForm: new Form({
        index: 0,
        subject: '',
      })
    }
  },
  computed: {
    ...mapGetters([
      'activePresentations',
      'activeUsers',
      'brands', // MLD-4796
      'canTestEmailTemplates', // MLD-4550
      'emailPreviewsEnabled',
      'folderTree', // MLD-2757
      'groups',
      'isCampaignsEnabled', // MLD-3917
      'isEmailComposerEnabled', // MLD-6517
      'isTeamUsingLegacyEmailComposer', // MLD-7126
      'isQuickShareEnabled', // MLD-3917
      'locales', // MLD-5385
      'presentations',
      'rootFolders', // MLD-6970
      'spamTestEnabled', // MLD-4012
      'team',
      'user',
      'users',
    ]),
    ...mapGetters('content', [
      'brands', // MLD-4796
    ]),
    showFolderField() { // MLD-6970 I re-enabled this for teams using the Outlook plugin.
      return this.team
        && (this.team.email_plugins_enabled || this.team.outlook_plugin_enabled)
        && this.rootFolders?.length > 1;
    },
    expirationValidationRules() { // MLD-4836
      if (this.team.share_template_expiration_required) {
        return [this.rules.required];
      }
      return [];
    },
    customMessageHint() { // MLD-3998
      return 'You need to include the {{ custom_message }} tag in the email body.';
    },
    isTinyMCE() { // MLD-2473
      return this.form.editor === 'tinymce';
    },
    isUnlayer() { // MLD-2473
      return this.form.editor === 'unlayer';
    },
    isRaw() { // MLD-2473
      return this.form.editor === 'raw';
    },
    canCreate() {
      if (this.template) {
        return false;
      }
      if (this.user.team_admin) {
        return true;
      }
      return this.team.member_share_templates;
    },
    canEdit() {
      if (this.user.team_admin) {
        return true;
      }
      return this.team.member_share_templates
        && this.template
        && this.user.id === this.template.user_id;
    },
    tinyConfig() {
      const config = {
        content_css: `/v2/css/tinymce_editor.css?v=${new Date().getTime()}`, // MLD-2694
        selector: '#tinyEditor',
        height: 700,
        //menubar: 'view insert format tools table help',
        menubar: 'mobilelocker edit view insert format tools table help', // @see https://www.tiny.cloud/docs/configure/editor-appearance/#menubar
        menu: { // @see https://www.tiny.cloud/docs/configure/editor-appearance/#menu
          //file: { title: 'File', items: 'newdocument restoredraft | preview | print ' },
          mobilelocker: {
            title: 'Mobile Locker',
            //items: 'menuitem_ml_insert_tag | toggle_menuitem_ml_lock | toggle_menuitem_ml_highlight_noneditable',
            items: 'toggle_menuitem_ml_highlight_noneditable',
          },
          edit: {title: 'Edit', items: 'undo redo | cut copy paste | selectall | searchreplace'},
          view: {title: 'View', items: 'code | visualaid visualchars visualblocks | spellchecker | preview fullscreen'},
          insert: {
            title: 'Insert',
            items: 'image link media template codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor toc | insertdatetime'
          },
          format: {
            title: 'Format',
            items: 'bold italic underline strikethrough superscript subscript codeformat | formats blockformats fontformats fontsizes align lineheight | forecolor backcolor | removeformat'
          },
          tools: {title: 'Tools', items: 'spellchecker spellcheckerlanguage | code wordcount'},
          table: {title: 'Table', items: 'inserttable | cell row column | tableprops deletetable'},
          help: {title: 'Help', items: 'help'}
        },
        noneditable_regexp: tinymceService.getNonEditableRegularExpressions(),
        //quickbars_insert_toolbar: 'ml_quick_insert', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#exampledisablingthequickinsertcontexttoolbar
        quickbars_insert_toolbar: false,
        quickbars_selection_toolbar: 'bold italic | quicklink', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#quickbars_selection_toolbar
        toolbar: 'button_ml_insert_tag | button_ml_insert_picker | toggle_button_ml_lock | undo redo | bold italic underline superscript subscript | numlist bullist | link image | removeformat | fontselect formatselect fontsizeselect | alignleft aligncenter alignright alignjustify',
        //contextmenu_never_use_native: true, // @see https://www.tiny.cloud/docs/ui-components/contextmenu/#contextmenu_never_use_native
        contextmenu: 'context_menu_ml_insert_tag | context_menu_ml_lock | link', // @see https://www.tiny.cloud/docs/ui-components/contextmenu/
        init_instance_callback: (editor) => {
          const elm = editor.dom.select('#tinymce')
          editor.dom.addClass(elm, 'EditMode');
          console.log('ShareTemplateForm TinyMCE Editor is now initialized');

          /*const pickers = editor.dom.select('span.mlPicker');
          pickers
            .filter(p => !editor.dom.hasClass(p, 'mlPickerConfigured'))
            .forEach(picker => {
              const select = document.createElement('select');
              editor.dom.addClass(select, 'mlPicker');
              const attributes = [
                'data-label',
                'data-required',
                'data-default',
                'data-options',
                'data-value',
              ]
              attributes.forEach(attribute => {
                editor.dom.setAttrib(select, attribute, editor.dom.getAttrib(picker, attribute));
              });

              const sibling = picker.nextSibling;
              if (!sibling || (sibling && sibling.nodeName !== 'SELECT' && editor.dom.hasClass(sibling, 'mlPicker'))) {
                picker.after(select);
                editor.dom.setAttrib(picker, 'style', 'display: none;');
                editor.dom.addClass(picker, 'mlPickerConfigured');
              }
            })*/

          const selects = editor.dom.select('select.mlPicker')
          selects
            .filter(s => !editor.dom.hasClass(s, 'mlPickerConfigured'))
            .forEach(select => {
              if (isNull(select.querySelector(`option[value=""]`))) {
                const defaultOption = document.createElement('option');
                const label = editor.dom.getAttrib(select, 'data-label');
                defaultOption.value = '';
                defaultOption.label = label;
                select.appendChild(defaultOption);
              }

              const options = editor.dom.getAttrib(select, 'data-options').split('|');
              options.forEach(option => {
                if (isNull(select.querySelector(`option[value="${option}"]`))) {
                  console.log('Adding option', option);
                  const opt = document.createElement('option');
                  opt.value = option;
                  opt.label = option;
                  select.appendChild(opt);
                }
              })

              editor.dom.addClass(select, 'mlPickerConfigured');
            })
        },
        setup: (editor) => {
          this.editor = editor;

          // MLD-7126, MLD-7257 Only show the Insert Dropdown for teams using the legacy email composer.
          if (this.isTeamUsingLegacyEmailComposer) {
            editor.ui.registry.addButton('button_ml_insert_picker', {
              text: 'Insert Dropdown',
              onSetup: (api) => {
                const element = editor.selection.getNode();
                api.setDisabled(mobilelockerEditor.isEditable(editor, element));
                editor.on('NodeChange', ({element}) => {
                  api.setDisabled(!mobilelockerEditor.isEditable(editor, element));
                });
              },
              onAction: (api) => {
                this.pickerDialog = true;
              }
            })
          }

          editor.ui.registry.addButton('button_ml_insert_tag', {
            text: 'Insert Tag',
            tooltip: 'Insert a tag at the current cursor position.',
            onSetup: (api) => {
              editor.on('NodeChange', ({element}) => {
                api.setDisabled(!mobilelockerEditor.isEditable(editor, element));
              });
            },
            onAction: (api) => {
              this.openTagsDialog();
            }
          })
          editor.ui.registry.addMenuItem('menuitem_ml_insert_tag', {
            icon: 'code-sample', // @see https://www.tiny.cloud/docs/advanced/editor-icon-identifiers/
            text: 'Insert tag at caret location',
            tooltip: 'Insert a tag at the current cursor position.',
            onSetup: (api) => {
              if (mobilelockerEditor.isPicker(editor, editor.selection.getNode())) {
                api.setDisabled(true);
              } else {
                api.setDisabled(!mobilelockerEditor.isEditable(editor, editor.selection.getNode()));
              }
              editor.on('NodeChange', ({element}) => {
                if (mobilelockerEditor.isPicker(editor, editor.selection.getNode())) {
                  api.setDisabled(true);
                } else {
                  api.setDisabled(!mobilelockerEditor.isEditable(editor, element));
                }
              });
            },
            onAction: () => {
              this.tagsDialog = true;
            }
          })
        },
      }
      return tinymceService.getConfig(config);
    },
  },
  created() {
    this.$store.dispatch('fetchPresentations')
    this.fetchData();
    this.changeEditor(); // MLD-3769
  },
  methods: {
    async fetchData() {
      this.busy = true;
      await this.fetchCustomTags();
      await this.fetchSubjectLines();
      this.busy = false;
    },
    async fetchCustomTags() {
      if (this.template?.id) {
        this.customTags = await shareTemplateService.getCustomTagsForTemplate(this.template.id);
      }
    },
    async fetchSubjectLines() { // MLD-6517
      if (this.template?.id) {
        this.subjectLines = await shareTemplateService.getSubjectLinesFor(this.template.id);
      }
    },
    insertSelectedTag(content) {
      this.closeTagsDialog();
      if (this.isRaw) { // MLD-3634
        clipboardService.copy(content)
          .then(() => {
            this.$toast.success('Copied tag to your clipboard. Paste it into the HTML where you need it.');
          })
      } else if (this.editor) {
        this.editor.insertContent(content);
      }
    },
    savePicker() {
      if (this.pickerForm.options.length === 0) {
        return false;
      }
      if (!this.pickerForm.name) {
        return false;
      }
      if (this.pickerForm.new_option !== '') {
        return false;
      }
      const form = this.pickerForm.data();
      console.log('savePicker', form);
      const select = document.createElement('select');
      select.name = form.name.trim();
      this.editor.dom.addClass(select, 'mlPicker');

      const option = document.createElement('option')
      option.value = '';
      option.label = form.name.trim();
      option.innerHTML = form.name.trim();
      select.appendChild(option);

      form.options.forEach(value => {
        const option = document.createElement('option')
        option.value = value.trim();
        option.label = value.trim();
        option.innerHTML = value.trim();
        select.appendChild(option);
      })
      const content = this.editor.serializer.serialize(select);
      console.log(content);
      this.editor.insertContent(content);
      this.pickerDialog = false;
      this.pickerForm.reset();
      this.$refs.pickerForm.resetValidation();
      //this.$refs.pickerForm.reset();
    },
    removeOption(option) {
      this.pickerForm.options = this.pickerForm.options.filter(opt => opt !== option);
      //this.pickerForm.options = this.pickerForm.options.splice(index, 1);
    },
    addOption() {
      const option = this.pickerForm.new_option.trim();
      if (option) {
        if (this.pickerForm.options.includes(option)) {
          this.$toast.error('You cannot have two options with the same value.');
          return;
        }
        this.pickerForm.options.push(option);
        this.pickerForm.new_option = '';
      }
    },
    copyURL(url) { // MLD-3464
      clipboardService.copy(url)
        .then(data => {
          this.$toast.success('URL copied to your clipboard.');
        })
    },
    closeTagsDialog() {
      this.tagsDialog = false;
    },
    openTagsDialog() { // MLD-3634
      this.tagsDialog = true;
    },
    changeEditor() { // MLD-3769
      /*if (this.isRaw) { // HTML emails are not editable and the links cannot be shared.
        this.form.is_editable = false;
        //this.form.can_share_links = false; // MLD-6998 I commented this out.
      }*/
    },
    changeIsEditable() { // MLD-3769
      /*if (this.form.is_editable) {

      } else {
        this.form.can_share_links = false;
      }*/
    },
    openCustomTagDialog(customTag) { // MLD-6454
      this.customTag = customTag;
      this.customTagDialog = true;
      console.log('openCustomTagDialog for', customTag?.name);
    },
    async closeCustomTagDialog() { // MLD-6454
      console.log('closeCustomTagDialog');
      await this.fetchData();
      this.customTag = null;
      this.customTagDialog = false;
    },
    updateSubjectLine(subjectLine) {
      if (isObject(subjectLine)) {
        this.form.subject = subjectLine.subject;
      }
    },
    async openSubjectLineDialog(index) { // MLD-6527
      const subjectLine = this.form.subjects[index];
      console.log('openSubjectLineDialog', index, subjectLine);
      if (subjectLine) {
        this.subjectForm.index = index;
        this.subjectForm.subject = subjectLine.subject;
        this.subjectLineDialog = true;
      }
    },
    closeSubjectLineDialog(subject) {
      this.subjectLineDialog = false;
      if (isString(subject) && subject) {
        this.form.subjects[this.subjectForm.index].subject = subject;
      }
      this.subjectForm.reset();
    },
    deleteSubject(index) {
      if (this.form.subjects.length > 1) {
        this.form.subjects.splice(index, 1);
      }
    },
    checkSubjectLine(index) {
      const subjectLine = this.form.subjects[index];
      if (isObject(subjectLine)) {
        if (!subjectLine.subject) {
          this.deleteSubject(index);
        }
      }
    },
    addAnotherSubject(index) {
      const subjectLine = this.form.subjects[index];
      if (isObject(subjectLine) && subjectLine.subject) {
        this.form.subjects.push({
          id: null,
          position: (index + 2),
          subject: '',
          team_id: this.team.id,
        });
      }

      if (this.form.subjects.length > 1) {
        this.form.is_subject_editable = false;
      }
    }
  }
}
</script>
